import { useContext, useEffect, useState } from "react";
import { Disclosure } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { getNavigationRoutes } from "../../config/router";
import { Link } from "react-router-dom";
import { classNames, isCurrentPath } from "../../utils";
import { RootContext } from "../../providers/RootProvider";
import SelectDropdown from "../SelectDropdown/SelectDropdown";
import { useFetchConfig } from "../../api/hooks";
import { S3Image } from "../../components/S3Image";
import { useTranslation } from "react-i18next";

const Header = () => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const {
    phases,
    selectedPhaseId,
    setSelectedPhaseId,
    selectedPhaseType,
    setSelectedPhaseType,
    filteredPhases,
  } = useContext(RootContext);
  const { data: config } = useFetchConfig();
  const routes = getNavigationRoutes(
    config?.is_live ?? false,
    config?.landing?.pages
  );
  const accentColor = config?.landing?.accent_color || "#FFA500";
  const { t } = useTranslation();
  const isRankingPage = isCurrentPath("/ranking");
  const isGamesPage = isCurrentPath("/games");
  const isStatsPage = isCurrentPath("/stats");

  const shouldShowPhaseSelector = isRankingPage || isGamesPage || isStatsPage;

  useEffect(() => {
    if (filteredPhases.length > 0 && !selectedPhaseId) {
      setSelectedPhaseId(filteredPhases[0]._id);
    }
  }, [filteredPhases, selectedPhaseId, setSelectedPhaseId]);

  // Reset selected phase when phase type changes
  useEffect(() => {
    if (filteredPhases.length > 0) {
      setSelectedPhaseId(filteredPhases[0]._id);
    } else {
      setSelectedPhaseId(undefined);
    }
  }, [selectedPhaseType, filteredPhases, setSelectedPhaseId]);

  const onSelectPhase = (value: string | undefined) => {
    setSelectedPhaseId(value);
  };

  const onSelectPhaseType = (value: string) => {
    setSelectedPhaseType(value as "regular" | "playoff" | "final");
  };

  // Get available phase types based on what phases exist
  const availablePhaseTypes = phases.reduce((acc, phase) => {
    const hasRegular = acc.some((type) => type.value === "regular");
    const hasPlayoff = acc.some((type) => type.value === "playoff");
    const hasFinal = acc.some((type) => type.value === "final");
    if (!hasRegular && phase.type === "regular") {
      acc.push({ value: "regular", label: t("header:regular") });
    } else if (!hasPlayoff && phase.type === "playoff") {
      acc.push({ value: "playoff", label: t("header:playoff") });
    } else if (!hasFinal && phase.type === "final") {
      acc.push({ value: "final", label: t("header:final") });
    }
    return acc;
  }, [] as { value: "regular" | "playoff" | "final"; label: string }[]);

  // Auto-select first available phase type if current type has no phases
  useEffect(() => {
    if (
      availablePhaseTypes.length > 0 &&
      !availablePhaseTypes.some((type) => type.value === selectedPhaseType)
    ) {
      setSelectedPhaseType(
        availablePhaseTypes[0].value as "regular" | "playoff" | "final"
      );
    }
  }, [availablePhaseTypes, selectedPhaseType, setSelectedPhaseType]);

  return (
    <>
      <div className="min-h-full">
        <Disclosure as="nav" className="border-b border-gray-200 bg-white">
          {({ open }) => (
            <>
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 justify-between">
                  <div className="flex">
                    {config?.logo_key ? (
                      <div className="flex flex-shrink-0 items-center">
                        <Link to="/">
                          <span className="sr-only">{config?.slug}</span>
                          <S3Image
                            alt={config?.slug}
                            className="h-10 w-auto sm:h-15"
                            keyPath={config?.logo_key}
                          />
                        </Link>
                      </div>
                    ) : null}
                    <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
                      {routes.map((item) => (
                        <Link
                          key={item.title}
                          to={item.path}
                          onMouseEnter={() => setHoveredItem(item.title)}
                          onMouseLeave={() => setHoveredItem(null)}
                          className={classNames(
                            isCurrentPath(item.path)
                              ? `text-gray-900`
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                            "inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                          )}
                          aria-current={
                            isCurrentPath(item.path) ? "page" : undefined
                          }
                          style={{
                            color: isCurrentPath(item.path)
                              ? accentColor
                              : hoveredItem === item.title
                              ? "#374151"
                              : "#6b7280",
                            borderColor: isCurrentPath(item.path)
                              ? accentColor
                              : hoveredItem === item.title
                              ? "#d1d5db"
                              : "transparent",
                          }}
                        >
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    {shouldShowPhaseSelector && (
                      <div className="flex flex-row items-center gap-2">
                        <SelectDropdown
                          options={availablePhaseTypes}
                          onSelect={onSelectPhaseType}
                          selected={selectedPhaseType}
                        />
                        <SelectDropdown
                          options={filteredPhases.map((phase) => ({
                            value: phase._id,
                            label: phase.name,
                          }))}
                          onSelect={onSelectPhase}
                          selected={selectedPhaseId}
                        />
                      </div>
                    )}
                  </div>
                  <div className="-mr-2 flex items-center sm:hidden">
                    {/* Mobile menu button */}
                    <Disclosure.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XMarkIcon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      ) : (
                        <Bars3Icon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="sm:hidden">
                <div className="space-y-1 pt-2 pb-3">
                  {routes.map((item) => (
                    <Disclosure.Button
                      key={item.title}
                      as="a"
                      href={item.path}
                      className={classNames(
                        isCurrentPath(item.path)
                          ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800",
                        "block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
                      )}
                      aria-current={
                        isCurrentPath(item.path) ? "page" : undefined
                      }
                    >
                      {item.title}
                    </Disclosure.Button>
                  ))}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
    </>
  );
};

export default Header;
